// Layout → panes, GL cell rendering, 2D overlay (crosshair, wipe handle) and DOM badges (F-04, F-05, F-06).

import type { Point, SourceId, ViewAdjustmentValues } from '../core/types';
import { GLRenderer, type CellParams, type Xf } from '../render/gl-renderer';
import type { Store } from './store';
import type { Sources } from './sources';
import type { Player, DisplayChoice } from './player';
import { browserAssumedSetup, correctionMatrix, isIdentityAdjustment } from '../core/color';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PaneView {
  /** Device px per comparison-grid px. */
  s: number;
  ox: number;
  oy: number;
}

export interface Pane {
  rect: Rect;
  kind: 'top' | 'zoom' | 'composite' | 'empty';
  src: SourceId;
  view: PaneView;
}

const BAND = { all: 0, low: 1, high: 2 } as const;
const BG: [number, number, number] = [14 / 255, 14 / 255, 14 / 255];
const TOP_BG: [number, number, number] = [22 / 255, 22 / 255, 22 / 255];
const ZOOM_OUTSIDE: [number, number, number] = [74 / 255, 74 / 255, 74 / 255];

export class View {
  readonly gl: GLRenderer;
  readonly overlay: CanvasRenderingContext2D;
  panes: Pane[] = [];
  dpr = window.devicePixelRatio || 1;
  width = 0;
  height = 0;
  /** Called after each render so DOM overlays / dock can refresh. */
  onRendered: (() => void) | null = null;
  lastDisplay: DisplayChoice | null = null;
  private queued = false;

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly overlayCanvas: HTMLCanvasElement,
    private store: Store,
    private sources: Sources,
    private player: Player,
  ) {
    this.gl = new GLRenderer(canvas);
    this.gl.onRestored = () => this.request();
    this.overlay = overlayCanvas.getContext('2d')!;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        let w: number;
        let h: number;
        const box = e.devicePixelContentBoxSize?.[0];
        if (box) {
          w = box.inlineSize;
          h = box.blockSize;
        } else {
          w = Math.round(e.contentRect.width * window.devicePixelRatio);
          h = Math.round(e.contentRect.height * window.devicePixelRatio);
        }
        this.dpr = window.devicePixelRatio || 1;
        this.width = w;
        this.height = h;
        this.request();
      }
    });
    try {
      ro.observe(canvas, { box: 'device-pixel-content-box' });
    } catch {
      ro.observe(canvas);
    }
  }

  request(): void {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      this.render();
    });
  }

  get k(): number {
    return this.store.state.project.compareScale;
  }

  /** A frame size in A pixels (the comparison base, D-37). */
  frameA(): { w: number; h: number } {
    const a = this.sources.source('A') ?? this.sources.source(this.sources.loadedIds()[0] ?? 'A');
    return a ? { w: a.spec.width, h: a.spec.height } : { w: 1920, h: 1080 };
  }

  grid(): { w: number; h: number } {
    const f = this.frameA();
    return { w: f.w * this.k, h: f.h * this.k };
  }

  gap(): number {
    return Math.max(2, Math.round(2 * this.dpr));
  }

  private splitH(r: Rect, g: number): [Rect, Rect] {
    const lw = Math.floor((r.w - g) / 2);
    return [
      { x: r.x, y: r.y, w: lw, h: r.h },
      { x: r.x + lw + g, y: r.y, w: r.w - lw - g, h: r.h },
    ];
  }

  private splitV(r: Rect, g: number): [Rect, Rect] {
    const th = Math.floor((r.h - g) / 2);
    return [
      { x: r.x, y: r.y, w: r.w, h: th },
      { x: r.x, y: r.y + th + g, w: r.w, h: r.h - th - g },
    ];
  }

  topBottom(): { top: Rect; bottom: Rect } {
    const g = this.gap();
    const topH = Math.round(this.height * this.store.state.project.layout.zoomPaneRatio);
    return { top: { x: 0, y: 0, w: this.width, h: topH }, bottom: { x: 0, y: topH + g, w: this.width, h: this.height - topH - g } };
  }

  /** Effective compare mode: flicker is rendered as single panes; alignment editing may force difference. */
  compareMode(): string {
    const L = this.store.state.project.layout;
    // Two-point alignment (F-03.11): A and the version must be seen apart to mark places on each.
    if (this.store.state.ui.alignPick) return 'side-by-side';
    if (this.store.state.ui.alignOpen && this.store.state.settings.alignAutoDifference && this.sources.has(L.selectedB)) return 'difference';
    return L.compareMode;
  }

  buildPanes(): Pane[] {
    const W = this.width;
    const H = this.height;
    const g = this.gap();
    const L = this.store.state.project.layout;
    const ui = this.store.state.ui;
    const full: Rect = { x: 0, y: 0, w: W, h: H };
    const panes: Pane[] = [];
    const has = (id: SourceId) => this.sources.has(id);
    const view = (rect: Rect, id: SourceId, kind: 'top' | 'zoom'): Pane => ({
      rect,
      kind: has(id) ? kind : 'empty',
      src: id,
      view: { s: 1, ox: 0, oy: 0 },
    });
    const selB = L.layout === 'focus' && L.focusSourceId && L.focusSourceId !== 'A' ? L.focusSourceId : L.selectedB;
    const mode = this.compareMode();
    const topArea = (rect: Rect) => {
      if (mode !== 'side-by-side' && has('A') && has(selB)) {
        panes.push({ rect, kind: 'composite', src: selB, view: { s: 1, ox: 0, oy: 0 } });
      } else {
        const [l, r] = L.sideBySideDirection === 'vertical' ? this.splitV(rect, g) : this.splitH(rect, g);
        panes.push(view(l, 'A', 'top'), view(r, selB, 'top'));
      }
    };
    // Before anything is loaded the default screen is just the two drop zones A｜B1 (F-01.1).
    const empty = this.sources.loadedIds().length === 0;
    if (ui.tempFull && has(ui.tempFull)) {
      panes.push(view(full, ui.tempFull, 'top'));
    } else if (empty && (L.layout === 'side-by-side-zoom' || L.layout === 'side-by-side' || L.layout === 'focus')) {
      const [l, r] = this.splitH(full, g);
      panes.push(view(l, 'A', 'top'), view(r, selB, 'top'));
    } else if (L.layout === 'single') {
      panes.push(view(full, L.focusSourceId ?? 'A', 'top'));
    } else if (L.layout === 'side-by-side' || L.layout === 'focus') {
      topArea(full);
    } else if (L.layout === 'side-by-side-zoom') {
      const { top, bottom } = this.topBottom();
      topArea(top);
      const [l, r] = this.splitH(bottom, g);
      panes.push(view(l, 'A', 'zoom'), view(r, selB, 'zoom'));
    } else {
      const lw = Math.floor((W - g) / 2);
      const th = Math.floor((H - g) / 2);
      const cells: Rect[] = [
        { x: 0, y: 0, w: lw, h: th },
        { x: lw + g, y: 0, w: W - lw - g, h: th },
        { x: 0, y: th + g, w: lw, h: H - th - g },
        { x: lw + g, y: th + g, w: W - lw - g, h: H - th - g },
      ];
      // F-05.10: overlay and flicker show each version over / alternating with A in its own pane; A stays plain.
      const blend = (mode === 'overlay' || mode === 'flicker') && has('A');
      (['A', 'B1', 'B2', 'B3'] as SourceId[]).forEach((id, i) =>
        panes.push(blend && id !== 'A' && has(id) ? { rect: cells[i], kind: 'composite', src: id, view: { s: 1, ox: 0, oy: 0 } } : view(cells[i], id, 'top')),
      );
    }
    for (const p of panes) p.view = p.kind === 'zoom' ? this.zoomView(p.rect) : this.topView(p.rect);
    return panes;
  }

  topView(r: Rect): PaneView {
    const { w: cw, h: ch } = this.grid();
    const v = this.store.state.project.layout.view;
    if (v.zoomPercent === 'fit') {
      const s = Math.min(r.w / cw, r.h / ch);
      return { s, ox: r.x + (r.w - cw * s) / 2, oy: r.y + (r.h - ch * s) / 2 };
    }
    const s = v.zoomPercent / 100;
    const c = { x: v.center.x * cw, y: v.center.y * ch };
    const cx = cw * s <= r.w ? (cw * s) / 2 : clamp(c.x * s, r.w / 2, cw * s - r.w / 2);
    const cy = ch * s <= r.h ? (ch * s) / 2 : clamp(c.y * s, r.h / 2, ch * s - r.h / 2);
    return { s, ox: Math.round(r.x + r.w / 2 - cx), oy: Math.round(r.y + r.h / 2 - cy) };
  }

  /** Zoom panes: integer N device px per grid px with the crosshair pixel centred (D-22). */
  zoomView(r: Rect): PaneView {
    const L = this.store.state.project.layout;
    const n = L.crosshair.zoomPercent / 100;
    const c = L.crosshair.position;
    const px = Math.floor(c.x * this.k);
    const py = Math.floor(c.y * this.k);
    return { s: n, ox: r.x + Math.floor(r.w / 2) - Math.floor(n / 2) - px * n, oy: r.y + Math.floor(r.h / 2) - Math.floor(n / 2) - py * n };
  }

  paneAt(pt: Point): Pane | null {
    return this.panes.find((p) => pt.x >= p.rect.x && pt.x < p.rect.x + p.rect.w && pt.y >= p.rect.y && pt.y < p.rect.y + p.rect.h) ?? null;
  }

  /** Device point in a pane → A pixel coordinates (D-33). */
  paneToA(pt: Point, p: Pane): Point {
    const v = p.view;
    return { x: (pt.x - v.ox) / v.s / this.k, y: (pt.y - v.oy) / v.s / this.k };
  }

  aToPane(a: Point, p: Pane): Point {
    const v = p.view;
    return { x: v.ox + a.x * this.k * v.s, y: v.oy + a.y * this.k * v.s };
  }

  /** Area covered by the zoom panes in A pixels (dashed box). */
  zoomRegionA(): Rect {
    const L = this.store.state.project.layout;
    const zp = this.panes.find((q) => q.kind === 'zoom');
    const n = L.crosshair.zoomPercent / 100;
    const w = zp ? zp.rect.w / n / this.k : 240;
    const h = zp ? zp.rect.h / n / this.k : 135;
    return { x: L.crosshair.position.x - w / 2, y: L.crosshair.position.y - h / 2, w, h };
  }

  /** Reference of the composite pane for version b (F-05.9): the chosen version when loaded and not b itself, else A. */
  compareBaseFor(b: SourceId): SourceId {
    const L = this.store.state.project.layout;
    // In the quad every version is compared with A, which stays in its own pane (F-05.10).
    if (L.layout === 'quad') return 'A';
    const base = L.compareBase;
    return base && base !== b && this.sources.has(base) ? base : 'A';
  }

  xfOf(id: SourceId): Xf {
    if (id === 'A') return { dx: 0, dy: 0, sx: 1, sy: 1 };
    const t = this.sources.source(id)?.spatialTransform;
    return t ? { dx: t.dx, dy: t.dy, sx: t.sx, sy: t.sy } : { dx: 0, dy: 0, sx: 1, sy: 1 };
  }

  adjFor(id: SourceId): ViewAdjustmentValues | null {
    const adj = this.store.state.project.viewAdjustment;
    if (!adj.enabled || isIdentityAdjustment(adj)) return null;
    if (!adj.linked && id !== this.store.state.project.layout.selectedB) return null;
    return adj;
  }

  /** Uploads the frames needed for the current tick. Returns the display choice. */
  private uploadFrames(): DisplayChoice {
    const d = this.player.display();
    for (const [id, f] of d.frames) {
      const c = this.sources.client(id);
      if (!c) continue;
      const src = this.sources.source(id)!;
      this.gl.setNativeSize(id, src.spec.width, src.spec.height);
      if (f === null) {
        this.gl.clearSource(id);
        continue;
      }
      const color = this.sources.color(id);
      const colorKey = `${color.matrix}/${color.range}`;
      const full = c.getFrame(f);
      if (full) {
        const key = `${f}|full|${colorKey}|${c.uid}`;
        if (this.gl.sourceKey(id) !== key) this.gl.uploadFrame(id, full, color, key);
        // Paused: the exact planes replace the GPU frame kept on screen by the player; release it now (E-15).
        if (!this.player.playing) c.retainGpu(-1, -1);
        continue;
      }
      const vf = c.getGpuFrame(f);
      if (vf) {
        const key = `${f}|gpu|${colorKey}|${c.uid}`;
        if (this.gl.sourceKey(id) !== key) this.gl.uploadVideoFrame(id, vf, correctionMatrix(browserAssumedSetup(vf.colorSpace), color), key);
        continue;
      }
      if (d.proxy.has(id)) {
        const bmp = c.getProxyBitmap(f);
        if (bmp) {
          const key = `${f}|proxy|${c.uid}`;
          if (this.gl.sourceKey(id) !== key) this.gl.uploadBitmap(id, bmp, key);
          continue;
        }
      }
      // Nothing of this frame is ready yet (scrubbing faster than decoding): keep this source's previous picture
      // instead of flashing black; the pane says "Decoding…" until the frame arrives (P-13). A picture of another
      // video (a shot switch) is never kept.
      d.pending.add(id);
      d.proxy.delete(id);
      if (!this.gl.sourceKey(id).endsWith(`|${c.uid}`)) this.gl.clearSource(id);
    }
    return d;
  }

  render(): void {
    if (!this.width || !this.height) return;
    const st = this.store.state;
    const L = st.project.layout;
    const d = this.uploadFrames();
    this.lastDisplay = d;
    this.panes = this.buildPanes();
    this.gl.begin(this.width, this.height, BG);
    const fa = this.frameA();
    const mode = this.compareMode();
    for (const p of this.panes) {
      if (p.kind === 'empty') continue;
      const v = p.view;
      const r = p.rect;
      const base: CellParams = {
        rect: r,
        mode: 'single',
        a: p.src,
        b: null,
        xfA: this.xfOf(p.src),
        xfB: { dx: 0, dy: 0, sx: 1, sy: 1 },
        frameA: fa,
        viewOrigin: { x: (r.x - v.ox) / v.s / this.k, y: (r.y - v.oy) / v.s / this.k },
        viewScale: 1 / (v.s * this.k),
        nearest: L.view.sampling === 'nearest' ? v.s * this.k >= 1 || p.kind === 'zoom' : false,
        outside: p.kind === 'zoom' ? ZOOM_OUTSIDE : TOP_BG,
        wipe: { pos: L.wipe.position, angleRad: (L.wipe.angleDeg * Math.PI) / 180 },
        opacity: L.overlayOpacity,
        diff: { gain: L.difference.gain, heat: L.difference.display === 'heat', luma: L.difference.lumaOnly, band: BAND[L.difference.band ?? 'all'] },
        channel: ({ rgb: 0, r: 1, g: 2, b: 3, luma: 4 } as const)[L.channel],
        texture: L.textureView,
        adjA: this.adjFor(p.src),
        adjB: null,
        ref: p.src !== 'A' && this.sources.has('A') ? 'A' : null,
        xfRef: this.xfOf('A'),
      };
      // Sampling below 100% uses smooth filtering for a readable overview; at or above 100% nearest (F-06.4).
      if (v.s * this.k < 1) base.nearest = false;
      if (p.kind === 'composite') {
        const b = p.src;
        const ref = this.compareBaseFor(b);
        if (mode === 'flicker') {
          const id = st.ui.flickerShowB ? b : ref;
          this.gl.drawCell({ ...base, a: id, xfA: this.xfOf(id), adjA: this.adjFor(id) });
        } else {
          this.gl.drawCell({
            ...base,
            mode: mode === 'wipe' ? 'wipe' : mode === 'overlay' ? 'overlay' : 'difference',
            a: ref,
            b,
            xfA: this.xfOf(ref),
            xfB: this.xfOf(b),
            adjA: this.adjFor(ref),
            adjB: this.adjFor(b),
          });
        }
      } else if (p.kind === 'zoom' && L.zoomDifference && p.src !== 'A' && this.sources.has('A')) {
        // F-06.6: the zoom pane of the B shows the difference instead.
        this.gl.drawCell({ ...base, mode: 'difference', a: 'A', b: p.src, xfA: this.xfOf('A'), xfB: this.xfOf(p.src), adjA: this.adjFor('A'), adjB: this.adjFor(p.src) });
      } else {
        this.gl.drawCell(base);
      }
    }
    this.drawOverlay();
    this.onRendered?.();
  }

  /**
   * Renders now and reads back RGBA pixels of the video layer (device px, top-left origin). Used by the still
   * export and by the automated sync tests (F-02.1): the pixels are exactly what the viewer shows.
   */
  readRegion(x: number, y: number, w: number, h: number): Uint8Array {
    this.render();
    return this.gl.readPixels(x, y, w, h);
  }

  // ---------------------------------------------------------------------------------------------- overlay

  private drawOverlay(): void {
    const oc = this.overlayCanvas;
    if (oc.width !== this.width) oc.width = this.width;
    if (oc.height !== this.height) oc.height = this.height;
    const ctx = this.overlay;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, oc.width, oc.height);
    const st = this.store.state;
    const L = st.project.layout;
    const mode = this.compareMode();
    for (const p of this.panes) {
      if (p.kind === 'empty') continue;
      ctx.save();
      ctx.beginPath();
      ctx.rect(p.rect.x, p.rect.y, p.rect.w, p.rect.h);
      ctx.clip();
      if (p.kind === 'zoom' && L.zoomGrid) this.drawZoomGrid(p);
      if (p.kind === 'zoom' && st.settings.zoomCenterMark) this.drawCenterMark(p);
      if ((p.kind === 'top' || p.kind === 'composite') && L.crosshair.visible) this.drawCrosshair(p);
      if (p.kind === 'composite' && mode === 'wipe') this.drawWipeHandle(p.rect);
      this.drawAlignPoints(p);
      ctx.restore();
    }
  }

  wipeLine(r: Rect): { px: number; py: number; theta: number } {
    const L = this.store.state.project.layout;
    // The shader splits at dot(uv - 0.5, n) = pos - 0.5 with n = (cos θ, sin θ) in pane-normalised units.
    const theta = (L.wipe.angleDeg * Math.PI) / 180;
    const off = L.wipe.position - 0.5;
    return { px: r.x + r.w * (0.5 + off * Math.cos(theta)), py: r.y + r.h * (0.5 + off * Math.sin(theta)), theta };
  }

  private drawWipeHandle(r: Rect): void {
    const ctx = this.overlay;
    const Lw = this.wipeLine(r);
    const dpr = this.dpr;
    // Direction along the split line in device px: perpendicular to n scaled by the pane aspect.
    const nx = Math.cos(Lw.theta) / r.w;
    const ny = Math.sin(Lw.theta) / r.h;
    let dx = -ny;
    let dy = nx;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const ext = Math.hypot(r.w, r.h);
    const lw = Math.max(1, Math.round(dpr));
    ctx.lineWidth = lw * 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.beginPath();
    ctx.moveTo(Lw.px - dx * ext, Lw.py - dy * ext);
    ctx.lineTo(Lw.px + dx * ext, Lw.py + dy * ext);
    ctx.stroke();
    ctx.lineWidth = lw;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.moveTo(Lw.px - dx * ext, Lw.py - dy * ext);
    ctx.lineTo(Lw.px + dx * ext, Lw.py + dy * ext);
    ctx.stroke();
    ctx.fillStyle = 'rgba(28,28,28,0.9)';
    ctx.beginPath();
    ctx.arc(Lw.px, Lw.py, 11 * dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#e8e8e8';
    ctx.font = `${Math.round(11 * dpr)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⇔', Lw.px, Lw.py + 0.5);
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
  }

  crossDevicePoint(p: Pane): Point {
    const c = this.store.state.project.layout.crosshair.position;
    const v = p.view;
    const g = { x: c.x * this.k, y: c.y * this.k };
    if (v.s >= 1) return { x: v.ox + (Math.floor(g.x) + 0.5) * v.s, y: v.oy + (Math.floor(g.y) + 0.5) * v.s };
    return { x: v.ox + g.x * v.s, y: v.oy + g.y * v.s };
  }

  /** Two-point alignment (F-03.11): numbered marks where the points were placed on A and on the version. */
  private drawAlignPoints(p: Pane): void {
    const pick = this.store.state.ui.alignPick;
    if (!pick || (p.src !== 'A' && p.src !== pick.id)) return;
    const tr = this.sources.source(pick.id)?.spatialTransform;
    if (!tr) return;
    const pts = p.src === 'A' ? pick.a : pick.b.map((q) => ({ x: tr.dx + q.x * tr.sx, y: tr.dy + q.y * tr.sy }));
    const ctx = this.overlay;
    const dpr = this.dpr;
    const r = 7 * dpr;
    ctx.font = `bold ${Math.round(11 * dpr)}px system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    pts.forEach((q, i) => {
      const d = this.aToPane(q, p);
      ctx.lineWidth = 3 * dpr;
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.beginPath();
      ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.lineWidth = 1.5 * dpr;
      ctx.strokeStyle = '#ffd400';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(d.x, d.y, 1.2 * dpr, 0, Math.PI * 2);
      ctx.fillStyle = '#ffd400';
      ctx.fill();
      ctx.lineWidth = 3 * dpr;
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.strokeText(String(i + 1), d.x + r + 2 * dpr, d.y - r);
      ctx.fillText(String(i + 1), d.x + r + 2 * dpr, d.y - r);
    });
  }

  private drawCrosshair(p: Pane): void {
    const ctx = this.overlay;
    const dpr = this.dpr;
    const d = this.crossDevicePoint(p);
    const lw = Math.max(1, Math.round(dpr));
    const x = Math.round(d.x) + (lw % 2 ? 0.5 : 0);
    const y = Math.round(d.y) + (lw % 2 ? 0.5 : 0);
    const gp = Math.round(6 * dpr);
    const arm = Math.round(22 * dpr);
    const segs = [
      [x - gp - arm, y, x - gp, y],
      [x + gp, y, x + gp + arm, y],
      [x, y - gp - arm, x, y - gp],
      [x, y + gp, x, y + gp + arm],
    ];
    for (const [w, color] of [
      [lw * 3, 'rgba(0,0,0,0.6)'],
      [lw, '#ffffff'],
    ] as [number, string][]) {
      ctx.lineWidth = w;
      ctx.strokeStyle = color;
      ctx.beginPath();
      for (const s of segs) {
        ctx.moveTo(s[0], s[1]);
        ctx.lineTo(s[2], s[3]);
      }
      ctx.stroke();
    }
    const L = this.store.state.project.layout;
    const zp = this.panes.find((q) => q.kind === 'zoom');
    if (zp && L.layout === 'side-by-side-zoom' && !this.store.state.ui.tempFull) {
      const n = L.crosshair.zoomPercent / 100;
      const zw = (zp.rect.w / n) * p.view.s;
      const zh = (zp.rect.h / n) * p.view.s;
      if (zw > 6 && zh > 6) {
        ctx.setLineDash([4 * dpr, 3 * dpr]);
        ctx.lineWidth = lw;
        ctx.strokeStyle = 'rgba(255,255,255,0.75)';
        ctx.strokeRect(Math.round(x - zw / 2) + 0.5, Math.round(y - zh / 2) + 0.5, Math.round(zw), Math.round(zh));
        ctx.setLineDash([]);
      }
    }
  }

  /** Pixel-comparison grid (F-06.10) in a zoom pane; same drawing as the export (drawPixelGrid). */
  private drawZoomGrid(p: Pane): void {
    const labels = this.store.state.project.layout.crosshair.zoomPercent >= ZOOM_GRID_LABEL_FROM;
    drawPixelGrid(this.overlay, p.rect, { x: p.view.ox, y: p.view.oy }, this.k * p.view.s, this.dpr, labels);
  }

  private drawCenterMark(p: Pane): void {
    const ctx = this.overlay;
    const L = this.store.state.project.layout;
    const n = L.crosshair.zoomPercent / 100;
    const c = L.crosshair.position;
    const x = p.view.ox + Math.floor(c.x * this.k) * n;
    const y = p.view.oy + Math.floor(c.y * this.k) * n;
    const lw = Math.max(1, Math.round(this.dpr));
    ctx.lineWidth = lw;
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.strokeRect(x - lw / 2, y - lw / 2, n + lw, n + lw);
  }
}

export function drawLabel(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  anchorIsPoint: boolean,
  dpr: number,
  outline = false,
): void {
  const fs = Math.round(14 * dpr);
  const padX = 6 * dpr;
  const padY = 4 * dpr;
  const lh = fs * 1.3;
  ctx.font = `600 ${fs}px system-ui, "Microsoft JhengHei", "PingFang TC", "Noto Sans TC", sans-serif`;
  const lines = text.split('\n');
  const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + padX * 2 + 3 * dpr;
  const h = lines.length * lh + padY * 2;
  const top = anchorIsPoint ? y : y - h;
  ctx.fillStyle = 'rgba(12,12,12,0.82)';
  ctx.fillRect(x, top, w, h);
  ctx.fillStyle = color;
  ctx.fillRect(x, top, 3 * dpr, h);
  if (outline) {
    ctx.setLineDash([3 * dpr, 3 * dpr]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#fff';
    ctx.strokeRect(x, top, w, h);
    ctx.setLineDash([]);
  }
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'top';
  lines.forEach((l, i) => ctx.fillText(l, x + padX + 3 * dpr, top + padY + i * lh + (lh - fs) / 2));
  ctx.textBaseline = 'alphabetic';
}

/** Grid spacing of the zoom-pane grid, in A pixels (F-06.10). */
const ZOOM_GRID_A_PX = 10;
/** Zoom level from which the grid intersections show their coordinates. */
export const ZOOM_GRID_LABEL_FROM = 2000;

/**
 * Pixel-comparison grid (F-06.10): lines every 10 A pixels on pixel boundaries, anchored at A (0, 0), so the left
 * and right zoom panes show the same grid over the same pixels; optionally each intersection is labelled with its
 * A pixel coordinates. `origin` is where A (0, 0) lands and `unit` the size of one A pixel, in canvas pixels.
 * Used by the viewer and by the internal export (F-13).
 */
export function drawPixelGrid(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  r: Rect,
  origin: Point,
  unit: number,
  scale: number,
  labels: boolean,
): void {
  const step = ZOOM_GRID_A_PX * unit;
  if (step < 4) return;
  const lw = Math.max(1, Math.round(scale));
  const x0 = origin.x + Math.ceil((r.x - origin.x) / step) * step;
  const y0 = origin.y + Math.ceil((r.y - origin.y) / step) * step;
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  for (let x = x0; x < r.x + r.w; x += step) ctx.fillRect(Math.round(x), r.y, lw, r.h);
  for (let y = y0; y < r.y + r.h; y += step) ctx.fillRect(r.x, Math.round(y), r.w, lw);
  // A dark companion line keeps the grid visible on bright content.
  ctx.fillStyle = 'rgba(0,0,0,0.11)';
  for (let x = x0; x < r.x + r.w; x += step) ctx.fillRect(Math.round(x) + lw, r.y, lw, r.h);
  for (let y = y0; y < r.y + r.h; y += step) ctx.fillRect(r.x, Math.round(y) + lw, r.w, lw);
  if (!labels) return;
  const pad = Math.round(3 * scale);
  ctx.font = `${Math.round(10 * scale)}px ui-monospace, Consolas, monospace`;
  ctx.textBaseline = 'top';
  ctx.lineWidth = Math.max(2, Math.round(2 * scale));
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  for (let y = y0; y < r.y + r.h; y += step) {
    const ay = Math.round((y - origin.y) / unit);
    for (let x = x0; x < r.x + r.w; x += step) {
      const label = `${Math.round((x - origin.x) / unit)},${ay}`;
      const tx = Math.round(x) + lw + pad;
      const ty = Math.round(y) + lw + pad;
      ctx.strokeText(label, tx, ty);
      ctx.fillText(label, tx, ty);
    }
  }
  ctx.textBaseline = 'alphabetic';
}

function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}
